import { zQueryBoolean } from "@common/schemas/query-boolean.schema";
import { FLAT_RATE_PLANS } from "@common/flatRateBilling";
import { UserWorkspaceRole } from "@common/userWorkspaceRoles";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { EntityProfileIndustry, EntityProfileType } from "@common/types/entity";
import { WorkspaceTimezone } from "@common/workspaceTimezone";
import { z } from "zod";

// Canonical, model-facing description of the `isFree` billing signal. Kept as a
// single exported constant so the schema and the agent tool descriptions cannot
// drift apart. `isFree` is billing/subscription-based and deliberately
// plan-agnostic; paid MCP/agent tools are gated by `plan`, not by `isFree`.
export const WORKSPACE_IS_FREE_DESCRIPTION =
    "isFree (billing signal, plan-agnostic): true when no active paid subscription (ACTIVE/TRIALING/PAST_DUE) exists on the billing that governs this workspace — the owning organization's billing when an organization covers it, otherwise the workspace's own. For an organization-covered workspace this can disagree with the workspace's Billing page, which reports any organization-covered workspace as subscribed. Paid MCP/agent tools are gated by the workspace's plan, not by isFree.";

const workspaceAccessMetadataSchema = z.object({
    workspaceRole: z.nativeEnum(UserWorkspaceRole),
    assignmentOrganizationId: z.string().uuid().nullable(),
    isSharedWorkspace: z.boolean(),
});

export const workspaceSchema = z.object({
    id: z.string(),
    name: z.string(),
    iconColor: z.string().nullable(),
    // Optional so a frontend deployed ahead of the backend that lists it still
    // validates, and so responses that don't project it (workspace context)
    // stay valid. Readers treat a missing zone as unknown.
    timezone: z.nativeEnum(WorkspaceTimezone).optional(),
    plan: z.nativeEnum(FLAT_RATE_PLANS).nullable(),
    createdAt: z.coerce.date(),
    organizationIds: z.array(z.string()),
    workspaceOwnsBilling: z.boolean(),
    isFirmWorkspace: z.boolean(),
    /**
     * @deprecated Mirrors `isFirmWorkspace` for browser bundles loaded before
     * the rename. Remove together with the `isOrganizationDemo` column.
     */
    isOrganizationDemo: z.boolean().optional(),
    access: workspaceAccessMetadataSchema,

    // Optional by requesting
    entities: z.array(
        z.object({
            id: z.number(),
            // Optional so a frontend deployed ahead of a backend that predates
            // the `uuid` projection still validates `includeEntities` responses.
            // The backend sets it whenever the projection is available.
            uuid: z.string().uuid().optional(),
            name: z.string().nullable(),
            iconColor: z.string(),
            workspaceId: z.string(),
            logo: z.string().nullable(),
            industries: z.array(z.nativeEnum(EntityProfileIndustry)),
            legalType: z.nativeEnum(EntityProfileType).nullable(),
            isPersonal: z.boolean(),
            isMockEntity: z.boolean(),
            hasCustomChartOfAccounts: z.boolean(),
            user: z
                .object({
                    id: z.number(),
                    name: z.string(),
                    email: z.string().nullable(),
                    avatar: z.string().nullable(),
                    iconColor: z.string(),
                })
                .nullable(),
        }),
    ),

    // Computed on BE
    organizationOwnsBilling: z.boolean().optional(),
    // Computed on BE (`glFirstEnabledAt != null`). Agent surfaces consume this
    // boolean to detect GL-first workspaces; never the timestamp field.
    glFirstEnabled: z.boolean().optional(),
    // Computed on BE only when `includeBillingStatus` is requested.
    // See WORKSPACE_IS_FREE_DESCRIPTION: billing-based and plan-agnostic; it is
    // NOT the feature-gating signal (paid MCP/agent tools are gated by `plan`).
    isFree: z.boolean().optional().describe(WORKSPACE_IS_FREE_DESCRIPTION),
});

export type WorkspaceSchema = z.infer<typeof workspaceSchema>;

export const workspacesListQuerySchema = z.object({
    organizationId: z.string().uuid().optional(),
    includeEntities: z.coerce.boolean().optional(),
    includeBillingStatus: z.coerce.boolean().optional(),
    excludeDemoClient: zQueryBoolean().optional(),
    search: z.string().trim().min(1).max(100).optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
    cursor: z.string().max(500).optional(),
});

export type WorkspacesListQuery = z.infer<typeof workspacesListQuerySchema>;

export const workspacesListResponseSchema = z.object({
    data: z.array(workspaceSchema),
    // Default to 0 so a frontend deployed ahead of a backend that predates
    // the `total` field still validates the response.
    total: z.number().int().nonnegative().default(0),
    nextCursor: z.string().optional(),
    // Default to `false` so a frontend deployed ahead of a backend that
    // predates the `hasMore` field still validates the response. The backend
    // continues to set `hasMore` explicitly.
    hasMore: z.boolean().default(false),
});

export type WorkspacesListResponse = z.infer<
    typeof workspacesListResponseSchema
>;

export const workspaceContextEntitySchema = z.object({
    id: z.number().int(),
    name: z.string().nullable(),
    iconColor: z.string(),
    isPersonal: z.boolean(),
    isMockEntity: z.boolean(),
});

export const workspaceContextLedgerSchema = z.object({
    id: z.string().uuid(),
    entityId: z.number().int(),
    entityName: z.string().nullable(),
    basis: z.nativeEnum(AccountingLedgerBasis),
    name: z.string(),
    isPrimary: z.boolean(),
});

export const workspaceContextAccountSchema = z.object({
    entityId: z.number().int(),
    entityName: z.string().nullable(),
    accountId: z.string().uuid(),
    code: z.number().int(),
    codeText: z.string().nullable().optional(),
    name: z.string(),
    type: z.string(),
    subtype: z.string(),
    isDisabled: z.boolean(),
});

export const workspaceContextResponseSchema = z.object({
    workspace: workspaceSchema.omit({ entities: true }),
    reportableEntities: z.array(workspaceContextEntitySchema),
    ledgers: z.array(workspaceContextLedgerSchema),
    accounts: z.array(workspaceContextAccountSchema),
    metadata: z.object({
        reportableEntityCount: z.number().int().nonnegative(),
        ledgerCount: z.number().int().nonnegative(),
        accountCount: z.number().int().nonnegative(),
        maxReportableEntities: z.number().int().positive(),
        maxAccounts: z.number().int().positive(),
        isReportableEntitiesTruncated: z.boolean(),
        isAccountsTruncated: z.boolean(),
    }),
});

export type WorkspaceContextResponse = z.infer<
    typeof workspaceContextResponseSchema
>;

export const updateWorkspacePreferencesBodySchema = z
    .object({
        showAccountNumbers: z.boolean().optional(),
        glFirstEnabled: z.boolean().optional(),
        timezone: z.nativeEnum(WorkspaceTimezone).optional(),
    })
    .refine((data) => Object.keys(data).length > 0, {
        message: "At least one preference must be provided",
    });

export type UpdateWorkspacePreferencesBody = z.infer<
    typeof updateWorkspacePreferencesBodySchema
>;
